'use client';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, keyframes } from '@mui/material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { useI18n } from '../../hooks';
import { GlassButton } from '../GlassButton';

const pulse = keyframes`
  0%   { transform: scale(1);   opacity: 1; }
  50%  { transform: scale(1.4); opacity: 0.5; }
  100% { transform: scale(1);   opacity: 1; }
`;

export interface AstorLiveToastProps {
  /** Open/closed (controlled). */
  open: boolean;
  /** Primary message line, e.g. "Drafted exception memo for". */
  message: ReactNode;
  /**
   * Optional mono accent string highlighted under the message — typically a
   * loan id (e.g. `LOAN-OMX-2026-0142`) rendered in the brand mint.
   */
  accent?: string;
  /** Follow-up line, e.g. "Ready for your review." */
  followup?: ReactNode;
  /** Astor model / version tag rendered in the header, e.g. "v4.2". */
  version?: string;
  /** Handler for the primary "Open" button. */
  onOpen?: () => void;
  /** Handler for "Dismiss" — typically flips `open` to false. */
  onDismiss?: () => void;
  /** Override the bottom offset (px). Defaults to 16. */
  bottomOffset?: number;
  /**
   * Override the left offset (px). Defaults to 78.
   *
   * 78px clears the portal's 70px rail (`RAIL_WIDTH` in `useSidebarRail`)
   * with an 8px gutter. Callers can override to match a different rail
   * width or a wider gutter.
   */
  leftOffset?: number;
  sx?: SxProps<Theme>;
}

/**
 * AstorLiveToast — bottom-left persistent running notification.
 *
 * Sits above any portal chrome (z-index 1300), with a pulsing live dot,
 * mono version chip, body text + mint loan-id accent, and two buttons:
 *   - Primary: `↗ Open` — fires `onOpen` (typically navigates to the case).
 *   - Secondary: `Dismiss` — fires `onDismiss`.
 *
 * Consumers control visibility via `open`. The toast is *persistent*
 * (no auto-dismiss timer) — Astor produces fresh activity until the user
 * clears it. Stacking multiple toasts is out of scope; render one at a time.
 */
export function AstorLiveToast({
  open,
  message,
  accent,
  followup,
  version,
  onOpen,
  onDismiss,
  bottomOffset = 16,
  // 78px = portal RAIL_WIDTH (70) + 8px gutter. Callers can override.
  leftOffset = 78,
  sx,
}: AstorLiveToastProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  // v3 design uses `primary.main` (#4ED9A4 in dark) for the brand mint accent
  // across the live-dot, border, and accent — matches sibling popovers
  // (BriefViewPopover, NotificationsPopover) and avoids the side-by-side hue
  // split that occurs when this toast pulls from `success.main` (an
  // intentionally offset shade).
  const mint = theme.palette.primary.main;
  // F.mintBorder per v3 design spec — the popovers use a dark/light split, but
  // the toast renders against the portal chrome and the spec calls for a
  // single, stronger border value here.
  const mintBorder = alpha(mint, 0.38);
  const mintGlow = alpha(mint, isDark ? 0.14 : 0.1);

  if (!open) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={[
        {
          position: 'fixed',
          bottom: bottomOffset,
          left: leftOffset,
          width: 210,
          zIndex: 1300,
          p: 1.25,
          px: 1.5,
          borderRadius: 1.25,
          border: `1px solid ${mintBorder}`,
          backgroundColor: 'background.paper',
          boxShadow: `0 12px 32px -10px rgba(0,0,0,0.55), inset 0 0 0 1px ${mintGlow}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.75,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box
            aria-hidden
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: mint,
              boxShadow: `0 0 6px ${mint}`,
              animation: `${pulse} 1.8s ease-in-out infinite`,
            }}
          />
          <Typography
            variant="overline"
            component="span"
            sx={{
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
              fontWeight: 600,
              color: mint,
              letterSpacing: '0.08em',
              lineHeight: 1,
            }}
          >
            {t('astor.live.label')}
          </Typography>
        </Box>
        {version && (
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
              color: 'text.disabled',
              lineHeight: 1,
            }}
          >
            {version}
          </Typography>
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: '0.75rem',
          color: 'text.primary',
          lineHeight: 1.45,
          mb: accent ? 0.25 : 0.5,
        }}
      >
        {message}
      </Typography>
      {accent && (
        <Typography
          component="div"
          sx={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: mint,
            mb: 0.5,
          }}
        >
          {accent}
        </Typography>
      )}
      {followup && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            mb: 1,
            lineHeight: 1.5,
          }}
        >
          {followup}
        </Typography>
      )}
      {/* Both handlers are optional — render only the buttons whose handlers
          a caller actually supplied, so the toast never presents a dead
          control. When neither is supplied the action row collapses. */}
      {(onOpen || onDismiss) && (
        <Box sx={{ display: 'flex', gap: 0.625 }}>
          {onOpen && (
            <GlassButton
              variant="contained"
              color="success"
              size="small"
              startIcon={<LaunchIcon />}
              onClick={onOpen}
              sx={{ flex: 1, minHeight: 32 }}
            >
              {t('astor.live.open')}
            </GlassButton>
          )}
          {onDismiss && (
            <GlassButton
              variant="text"
              size="small"
              onClick={onDismiss}
              sx={{ minHeight: 32, color: 'text.secondary' }}
            >
              {t('astor.live.dismiss')}
            </GlassButton>
          )}
        </Box>
      )}
    </Box>
  );
}

AstorLiveToast.displayName = 'AstorLiveToast';

// Re-export so consumers can replace the default icon if they need an alt brand mark.
export { AutoAwesomeIcon as AstorLiveDefaultIcon };
